import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Maximize2, Play } from "lucide-react";
import { GalleryItem } from "@/data/projects";

interface MosaicGalleryProps {
  items: GalleryItem[];
  fallbackImage?: string;
  accentColor?: string;
  onItemClick: (index: number) => void;
}

type LayoutItem = GalleryItem & {
  aspectRatio: number;
  orientation: "portrait" | "landscape" | "square";
};

function classifyOrientation(ratio: number): LayoutItem["orientation"] {
  if (ratio < 0.82) return "portrait";
  if (ratio > 1.25) return "landscape";
  return "square";
}

function useGalleryLayout(items: GalleryItem[]) {
  const [layoutItems, setLayoutItems] = useState<LayoutItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadItem = (item: GalleryItem): Promise<LayoutItem> =>
      new Promise((resolve) => {
        if (item.type === "video") {
          resolve({
            ...item,
            aspectRatio: 16 / 9,
            orientation: "landscape",
          });
          return;
        }

        const img = new Image();
        img.onload = () => {
          const ratio = img.naturalWidth / img.naturalHeight;
          resolve({
            ...item,
            aspectRatio: ratio,
            orientation: classifyOrientation(ratio),
          });
        };
        img.onerror = () => {
          resolve({
            ...item,
            aspectRatio: 4 / 3,
            orientation: "landscape",
          });
        };
        img.src = item.url;
      });

    setIsLoading(true);
    Promise.all(items.map(loadItem)).then((loaded) => {
      if (!cancelled) {
        setLayoutItems(loaded);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [items]);

  return { layoutItems, isLoading };
}

function getGridSpan(
  item: LayoutItem,
  index: number,
  total: number,
): string {
  if (index === 0 && total > 2) {
    return "sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2";
  }

  if (item.orientation === "portrait") {
    return "sm:row-span-2";
  }

  if (item.orientation === "landscape") {
    return "sm:col-span-2";
  }

  return "";
}

function MosaicSkeleton() {
  return (
    <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="mb-4 break-inside-avoid animate-pulse rounded-xl bg-muted/50"
          style={{ height: i % 2 === 0 ? 220 : 320 }}
        />
      ))}
    </div>
  );
}

export function MosaicGallery({
  items,
  fallbackImage,
  accentColor = "#6366F1",
  onItemClick,
}: MosaicGalleryProps) {
  const { layoutItems, isLoading } = useGalleryLayout(items);

  if (isLoading) {
    return <MosaicSkeleton />;
  }

  return (
    <>
      {/* Mobile & tablet: masonry preserves each image's natural proportions */}
      <div className="columns-1 gap-4 sm:columns-2 lg:hidden">
        {layoutItems.map((item, index) => {
          const thumb =
            item.type === "video" ? item.thumbnail || fallbackImage || item.url : item.url;

          return (
            <MosaicTile
              key={`${item.url}-m-${index}`}
              item={item}
              index={index}
              total={layoutItems.length}
              thumb={thumb}
              accentColor={accentColor}
              onItemClick={onItemClick}
              variant="masonry"
            />
          );
        })}
      </div>

      {/* Desktop: dense bento grid sized from image orientation */}
      <div className="hidden auto-rows-[minmax(180px,auto)] grid-cols-4 gap-4 lg:grid lg:grid-flow-dense">
        {layoutItems.map((item, index) => {
          const thumb =
            item.type === "video" ? item.thumbnail || fallbackImage || item.url : item.url;

          return (
            <MosaicTile
              key={`${item.url}-d-${index}`}
              item={item}
              index={index}
              total={layoutItems.length}
              thumb={thumb}
              accentColor={accentColor}
              onItemClick={onItemClick}
              variant="grid"
              gridSpan={getGridSpan(item, index, layoutItems.length)}
            />
          );
        })}
      </div>
    </>
  );
}

interface MosaicTileProps {
  item: LayoutItem;
  index: number;
  total: number;
  thumb: string;
  accentColor: string;
  onItemClick: (index: number) => void;
  variant: "masonry" | "grid";
  gridSpan?: string;
}

function MosaicTile({
  item,
  index,
  total,
  thumb,
  accentColor,
  onItemClick,
  variant,
  gridSpan = "",
}: MosaicTileProps) {
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.35) }}
      onClick={() => onItemClick(index)}
      className={`group relative w-full overflow-hidden rounded-xl border border-border/50 bg-muted/20 text-left transition-all duration-300 hover:border-foreground/25 hover:shadow-md ${
        variant === "masonry" ? "mb-4 break-inside-avoid" : `h-full min-h-[180px] ${gridSpan}`
      }`}
      style={{ "--accent": accentColor } as React.CSSProperties}
    >
      <div
        className={`relative w-full ${variant === "grid" ? "h-full min-h-[180px]" : ""}`}
        style={variant === "masonry" ? undefined : { aspectRatio: item.aspectRatio }}
      >
        <img
          src={thumb}
          alt={item.caption || `Gallery image ${index + 1}`}
          loading="lazy"
          className={`block w-full transition-transform duration-500 group-hover:scale-[1.02] ${
            variant === "grid" ? "h-full object-contain p-2" : "h-auto"
          }`}
        />

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        {item.type === "video" && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 shadow-md transition-transform duration-300 group-hover:scale-110">
              <Play className="h-4 w-4 translate-x-0.5 fill-black text-black" />
            </span>
          </span>
        )}

        <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background/85 opacity-0 shadow-sm backdrop-blur-sm transition-all duration-300 group-hover:opacity-100">
          <Maximize2 className="h-3.5 w-3.5 text-foreground" />
        </span>

        <div className="absolute inset-x-0 bottom-0 translate-y-2 p-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 sm:p-4">
          {item.caption && (
            <p className="text-xs font-medium text-white sm:text-sm">{item.caption}</p>
          )}
          <p className={`font-mono text-[10px] uppercase tracking-wider text-white/60 ${item.caption ? "mt-0.5" : ""}`}>
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </p>
        </div>
      </div>

      <span
        className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 scale-x-0 bg-[var(--accent)] transition-transform duration-300 group-hover:scale-x-100"
        aria-hidden
      />
    </motion.button>
  );
}
